"use client"
import React, { lazy, Suspense, useCallback, useEffect, useState, useMemo } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import AppLayout, { GlobalJobNotifier } from "./AppLayout";
import MyPage from "./pages/MyPage";
import SalesList from "./pages/SalesList";
import Templates from "./pages/Templates";
import SendPage from "./pages/SendPage";
import SettingsPage from "./pages/SettingsPage";
import HistoryPage from "./pages/HistoryPage";
import ClickLogsPage from "./pages/ClickLogsPage";
import DealsPage from "./pages/DealsPage";
import CustomerLedgerPage from "./pages/CustomerLedgerPage"; 
import UsersPage from "./pages/UsersPage";
import LoginPage from "./pages/LoginPage";
import OrdersPage from "./pages/OrdersPage";
import SendingPage from "./pages/SendingPage";
import AdminAnnouncementsPage from "./pages/AdminAnnouncementsPage";
import ApiKeysPage from "./pages/ApiKeysPage";
import ApiKeysEnPage from "./pages/ApiKeysEnPage";
import AccountPage from "./pages/AccountPage";
import FreePlanSurveyPage from "./pages/FreePlanSurveyPage";
import SignupPage from "./pages/SignupPage";
import SignupThanksPage from "./pages/SignupThanksPage";
import PlanSelectionPage from "./pages/PlanSelectionPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import InquiryManagementPage from "./pages/InquiryManagementPage";
import ClientManagementPage from "./pages/ClientManagementPage";
import ListTemplatesPage from "./pages/ListTemplatesPage";
import EditListTemplatePage from "./pages/EditListTemplatePage";
import PendingUsersPage from "./pages/PendingUsersPage";
import HelpPage from "./pages/HelpPage";
import VpsStatusDashboard from "./pages/VpsStatusDashboard";
import VpsManagementPage from "./pages/VpsManagementPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ClientSalesListPage from "./pages/ClientSalesListPage";
import ReviewDashboard from "./pages/ReviewDashboard";
import ReviewDetailPage from "./pages/ReviewDetailPage";
import InvoicesPage from "./pages/InvoicesPage";
import ListDownloadPage from "./pages/ListDownloadPage";
import AdminLearningQueue from "./pages/AdminLearningQueue";
import ExtensionPage from "./pages/ExtensionPage";
import QuotationViewPage from "./pages/QuotationViewPage";
import SubscriptionViewPage from "./pages/SubscriptionViewPage";
import SalesAnalytics from "./pages/SalesAnalytics"; 
import { getToken, getUser, getAuthHeader } from "./utils/auth";
import MarketingFormsPage from "./pages/MarketingFormsPage";
import MarketingLeadsPage from "./pages/MarketingLeadsPage";
import PublicFormPage from "./pages/PublicFormPage";
import MarketingEmailPage from "./pages/MarketingEmailPage";
import MarketingTrackingPage from "./pages/MarketingTrackingPage"; 
import MarketingScenariosPage from "./pages/MarketingScenariosPage"; 
import MarketingLpPage from "./pages/MarketingLpPage"; 
import MarketingMetaDashboard from "./pages/MarketingMetaDashboard";
import MarketingPresentationPage from "./pages/MarketingPresentationPage";
import PublicSignPage from './pages/PublicSignPage';
import BookingSettingsPage from "./pages/booking/BookingSettingsPage";
import PublicBookingPage from "./pages/public/PublicBookingPage";
import PublicBookingCompletePage from "./pages/public/PublicBookingCompletePage";
import PublicBookingManagePage from "./pages/public/PublicBookingManagePage";
// ★ メニュー定義の権限チェック関数をインポート
import {
  BOOKING_MANAGER_ROLE,
  getDefaultAuthenticatedPath,
  hasPermission,
  isOemBillingPath,
  isOemRestrictedPath,
  shouldSkipSubscriptionCheck
} from "./constants/menuDefs";
import { isMainSalesBreakerHost, withOperatorSurface } from "./utils/operatorMode";
import { API_KEYS_SURFACE_ROLES, canViewApiKeysSurface, canViewOemApiKeysSurface } from "./utils/apiSurfaceAccess";
import Invite from "./pages/Invite";
import UiSurfaceBoundary from "./uiux-runtime/UiSurfaceBoundary";
import UiSurfaceEntryRedirect from "./uiux-runtime/UiSurfaceEntryRedirect";
import UiUxPilotErrorBoundary from "./uiux-runtime/UiUxPilotErrorBoundary";
import { UIUX_THREE_TENANT_PILOT_RELEASE_ID } from "./uiux-runtime/uiuxPilotRelease";
import { mergeActiveJobs, mergeJobStatusUpdates } from "./uiux-runtime/jobState";
import { useLiveMountDecision } from "./uiux-live/liveMountGate";
import PilotIdentityGate, { usePilotIdentity } from "./uiux-live/PilotIdentityGate";

const UiUxRuntimeApp = lazy(() => import("./uiux-runtime/UiUxRuntimeApp"));
// Live wiring pilot: mounted in place of UiUxRuntimeApp ONLY when the
// server's bootstrap workspaces map marks any workspace 'live'.
const UiUxLiveApp = lazy(() => import("./uiux-live/UiUxLiveApp"));
// 2026-08-18オーナー要望: role='admin'（本家管理者）も新UIを利用可能に。
const UIUX_PILOT_ROLES = Object.freeze(["client_admin", "admin"]);

// 新UI(ライブ)は画面遷移でURLを /app/ui-preview/... に書き換えるが、この
// パスは開発プレビュー専用で本番ルートが無く、リロードすると未知パス扱い
// →ログインへ飛ばされていた(実地検証 2026-08-18)。/new側へ橋渡しして復帰
// させる。/new配下は未知サブパスを/new/homeへ自己正規化するため安全。
const UiPreviewReloadBridge = () => {
  const location = useLocation();
  const target = location.pathname.replace(/^\/ui-preview/, "/new") + location.search + location.hash;
  return <Navigate to={target} replace />;
};
const UIUX_WORKSPACES = new Set(["home", "targets", "crm", "marketing", "settings"]);

export const UIUX_FUNCTIONAL_TOOLS = Object.freeze({
  "form-outreach": { workspace: "targets", path: "form-outreach", label: "フォーム営業", description: "対象、文面、送信条件を確認してフォーム営業を開始します。", component: SendPage },
  "recipient-lists": { workspace: "targets", path: "recipient-lists", label: "送付先リスト", description: "送付先リストの作成、編集、削除を行います。", component: ListTemplatesPage },
  templates: { workspace: "targets", path: "templates", label: "文面テンプレート", description: "フォーム営業で使う文面を作成、編集、複製、削除します。", component: Templates, componentProps: { settingsPath: "/new/settings/sales-settings" } },
  "send-history": { workspace: "targets", path: "send-history", label: "送信状況・履歴", description: "進行中の処理と過去の送信結果を確認します。", component: HistoryPage, componentProps: { accountPath: "/new/settings/subscription" } },
  "list-download": { workspace: "targets", path: "list-download", label: "リストダウンロード", description: "条件を指定して営業リストのCSVを作成します。", component: ListDownloadPage },
  "sales-targets": { workspace: "crm", path: "sales-targets", label: "営業先", description: "営業先の追加、編集、CSV取込、削除を行います。", component: ClientSalesListPage, componentProps: { trackingPath: "/app/new/marketing/measurement" } },
  leads: { workspace: "crm", path: "leads", label: "リード", description: "リードの追加、編集、ステータス更新を行います。", component: MarketingLeadsPage, componentProps: { dealsPath: "/app/new/crm/pipeline", accountPath: "/new/settings/subscription" } },
  pipeline: { workspace: "crm", path: "pipeline", label: "案件管理", description: "案件の作成、編集、進捗更新を行います。", component: DealsPage, componentProps: { accountPath: "/new/settings/subscription" } },
  customers: { workspace: "crm", path: "customers", label: "顧客CRM", description: "顧客情報、対応履歴、関連データを管理します。", component: CustomerLedgerPage, componentProps: { accountPath: "/new/settings/subscription" } },
  performance: { workspace: "crm", path: "performance", label: "実績", description: "営業実績を確認し、活動実績を登録・更新します。", component: SalesAnalytics },
  email: { workspace: "marketing", path: "email", label: "メール配信", description: "メールの下書き、テスト送信、配信設定を管理します。", component: MarketingEmailPage, componentProps: { customersPath: "/new/crm/customers", leadsPath: "/new/crm/leads", trackingPath: "/new/marketing/measurement" } },
  scenarios: { workspace: "marketing", path: "scenarios", label: "シナリオ配信", description: "自動配信シナリオを作成、編集、開始・停止します。", component: MarketingScenariosPage },
  forms: { workspace: "marketing", path: "forms", label: "フォーム作成", description: "問い合わせ・申込フォームを作成、編集、公開します。", component: MarketingFormsPage },
  "hp-lp": { workspace: "marketing", path: "hp-lp", label: "HP・LP作成・公開", description: "HP・LPと画像素材を作成、編集、公開します。", component: MarketingLpPage },
  documents: { workspace: "marketing", path: "documents", label: "資料作成", description: "営業資料を作成、編集、公開します。", component: MarketingPresentationPage },
  booking: { workspace: "marketing", path: "booking", label: "日程調整", description: "予約ページと受付枠を設定します。", component: BookingSettingsPage, allowedRoles: ["client_admin"] },
  measurement: { workspace: "marketing", path: "measurement", label: "反応・アクセス", description: "反応、アクセス、流入データを確認します。", component: MarketingTrackingPage, componentProps: { defaultTab: "tracking" } },
  "sales-settings": { workspace: "settings", path: "sales-settings", label: "営業・マーケティング設定", description: "会社情報、送信条件、NG条件を変更・保存します。", component: SettingsPage },
  organization: { workspace: "settings", path: "organization", label: "会社・メンバー", description: "メンバー、権限、アカウントを管理します。", component: UsersPage },
  integrations: { workspace: "settings", path: "integrations", label: "外部サービス", description: "送信元、Meta、Stripeなどの外部サービス連携を管理します。", component: MarketingTrackingPage, componentProps: { defaultTab: "sender" } },
  subscription: { workspace: "settings", path: "subscription", label: "契約・プラン", description: "契約状態、プラン、実行枠、請求設定を管理します。", component: AccountPage },
  billing: { workspace: "settings", path: "billing", label: "利用明細", description: "支払い履歴と請求書を確認・出力します。", component: InvoicesPage },
  api: { workspace: "settings", path: "api", label: "外部ツール・AI", description: "APIキーと外部ツールの利用範囲を管理します。", component: ApiKeysPage, requireApiKeysSurfaceAccess: true },
  help: { workspace: "settings", path: "help", label: "ヘルプ", description: "使い方を確認し、必要な問い合わせを行います。", component: HelpPage },
});

const UIUX_FUNCTIONAL_TOOL_LIST = Object.entries(UIUX_FUNCTIONAL_TOOLS);

const AgencyClosed = () => (
  <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-8">
    <div className="max-w-xl bg-slate-900 border border-slate-800 rounded-3xl p-10 shadow-2xl text-center">
      <div className="text-xs font-black text-indigo-300 uppercase tracking-widest mb-3">Partner portal moved</div>
      <h1 className="text-2xl font-black tracking-tight mb-4">代理店ポータルはOEM側URLに移動しました</h1>
      <p className="text-slate-400 text-sm leading-relaxed">
        この代理店導線は正式利用を停止しています。OEMから案内された専用URLでログインしてください。
      </p>
      <a
        href="/app/login"
        className="inline-flex mt-8 bg-indigo-600 hover:bg-indigo-500 px-6 py-3 rounded-2xl font-black text-sm transition-colors"
      >
        ログインへ戻る
      </a>
    </div>
  </div>
);

const OemUnavailable = () => (
  <div className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center p-8">
    <div className="max-w-lg bg-white border border-slate-200 rounded-3xl p-10 shadow-xl text-center">
      <div className="text-xs font-black text-indigo-500 uppercase tracking-widest mb-3">Partner account</div>
      <h1 className="text-2xl font-black tracking-tight mb-4">ご利用開始前です</h1>
      <p className="text-slate-500 text-sm leading-relaxed">
        アカウントの有効化状況を確認しています。ご案内元の担当窓口へお問い合わせください。
      </p>
      <a
        href="/app/login"
        className="inline-flex mt-8 bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-3 rounded-2xl font-black text-sm transition-colors"
      >
        ログインへ戻る
      </a>
    </div>
  </div>
);



const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

const isOemHost = () => !isMainSalesBreakerHost(window.location.hostname);
const shouldBlockOemPublicBilling = (path) => isOemHost() && isOemBillingPath(path);

const LOCAL_DEV_AUTH_BYPASS =
  window.location.hostname === "localhost" &&
  process.env.REACT_APP_ENABLE_LOCAL_DEV_AUTH !== "false";

const AuthenticatedIndexRedirect = () => (
  <Navigate to={getDefaultAuthenticatedPath(getUser())} replace />
);

export const getUiUxWorkspaceFromPath = (pathname) => {
  const segments = String(pathname || "").split("/").filter(Boolean);
  const newIndex = segments[0] === "new" ? 0 : (segments[0] === "app" && segments[1] === "new" ? 1 : -1);
  const candidate = newIndex >= 0 ? segments[newIndex + 1] : null;
  return UIUX_WORKSPACES.has(candidate) ? candidate : "home";
};

export const resolveUiUxFunctionalRoute = (pathname) => {
  const segments = String(pathname || "").split("/").filter(Boolean);
  const newIndex = segments[0] === "new" ? 0 : (segments[0] === "app" && segments[1] === "new" ? 1 : -1);
  if (newIndex < 0) return { valid: false, workspace: "home", tool: null };

  const workspace = segments[newIndex + 1] || "home";
  if (!UIUX_WORKSPACES.has(workspace)) return { valid: false, workspace: "home", tool: null };
  const toolPath = segments.slice(newIndex + 2).join("/");
  if (!toolPath) return { valid: true, workspace, tool: null };

  if (workspace === "targets" && toolPath === "recipient-lists/new") {
    return {
      valid: true,
      workspace,
      tool: {
        id: "recipient-list-editor",
        label: "送付先リストを作成",
        description: "条件を選び、名前を付けて送付先リストとして保存します。",
        component: EditListTemplatePage,
        componentProps: { templateId: null, returnPath: "/new/targets/recipient-lists" },
      },
    };
  }

  const recipientEditMatch = workspace === "targets"
    ? toolPath.match(/^recipient-lists\/edit\/([1-9]\d*)$/)
    : null;
  if (recipientEditMatch) {
    return {
      valid: true,
      workspace,
      tool: {
        id: "recipient-list-editor",
        label: "送付先リストを編集",
        description: "保存済みの条件と対象を更新します。",
        component: EditListTemplatePage,
        componentProps: {
          templateId: recipientEditMatch[1],
          returnPath: "/new/targets/recipient-lists",
        },
      },
    };
  }

  const matched = UIUX_FUNCTIONAL_TOOL_LIST.find(([, definition]) => (
    definition.workspace === workspace && definition.path === toolPath
  ));
  if (!matched) return { valid: false, workspace, tool: null };
  const [id, definition] = matched;
  return { valid: true, workspace, tool: { id, ...definition } };
};

const UiUxPilotContextLayout = () => {
  // Identity-aware snapshot: on a cold load where the legacy bootstrap has
  // not written userInfo yet, this re-resolves once it lands instead of
  // freezing a null user for the lifetime of the layout.
  const identity = usePilotIdentity({ pilotRoles: UIUX_PILOT_ROLES });
  const user = identity.user;
  const clientId = user?.client_id || null;
  const [activeJobs, setActiveJobs] = useState([]);
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(true);

  const setActiveJob = useCallback((newJob) => {
    if (!newJob?.id) return;
    setActiveJobs((current) => {
      const existing = current.some((job) => String(job.id) === String(newJob.id));
      return existing
        ? current.map((job) => (String(job.id) === String(newJob.id) ? { ...job, ...newJob } : job))
        : [...current, { progress: 0, total: "...", result_message: "", ...newJob }];
    });
  }, []);

  useEffect(() => {
    let active = true;
    let timer = null;
    const refreshOperationalState = async () => {
      try {
        const [maintenanceResponse, jobsResponse] = await Promise.all([
          fetch(`${API_URL}/api/maintenance/status`, { headers: getAuthHeader() }),
          clientId
            ? fetch(`${API_URL}/api/jobs/active?client_id=${encodeURIComponent(clientId)}`, { headers: getAuthHeader() })
            : Promise.resolve(null),
        ]);
        const maintenance = await maintenanceResponse.json();
        const jobs = jobsResponse ? await jobsResponse.json() : { success: true, jobs: [] };
        if (!active) return;
        setIsMaintenanceMode(maintenanceResponse.ok && maintenance.success
          ? maintenance.maintenanceMode === true
          : true);
        if (jobsResponse?.ok && jobs.success && Array.isArray(jobs.jobs)) {
          setActiveJobs((current) => mergeActiveJobs(current, jobs.jobs));
        }
      } catch (_error) {
        if (active) setIsMaintenanceMode(true);
      }
    };
    refreshOperationalState();
    timer = setInterval(refreshOperationalState, 5_000);
    return () => {
      active = false;
      if (timer) clearInterval(timer);
    };
  }, [clientId]);

  const trackedActiveJobIds = useMemo(() => activeJobs
    .filter((job) => job.id && (job.status === "pending" || job.status === "processing" || job.status === "starting"))
    .map((job) => String(job.id))
    .sort()
    .join(","), [activeJobs]);

  useEffect(() => {
    if (!trackedActiveJobIds) return undefined;
    let active = true;
    const refreshTrackedJobs = async () => {
      const ids = trackedActiveJobIds.split(",").filter(Boolean);
      const results = await Promise.all(ids.map(async (id) => {
        try {
          const response = await fetch(`${API_URL}/api/jobs/status/${encodeURIComponent(id)}`, { headers: getAuthHeader() });
          const payload = await response.json();
          return response.ok && payload.success && payload.job ? payload.job : null;
        } catch (_error) {
          return null;
        }
      }));
      if (!active) return;
      setActiveJobs((current) => mergeJobStatusUpdates(current, results));
    };
    refreshTrackedJobs();
    const timer = setInterval(refreshTrackedJobs, 5_000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [trackedActiveJobIds]);

  const handleCancelJob = useCallback(async (jobId) => {
    try {
      const response = await fetch(`${API_URL}/api/jobs/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify({ jobId }),
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.error || "キャンセルの受付に失敗しました。");
      setActiveJobs((current) => current.filter((job) => String(job.id) !== String(jobId)));
      window.alert("ジョブのキャンセルを受け付けました。");
    } catch (error) {
      window.alert(`エラー: ${error.message}`);
    }
  }, []);

  const handleClearFinishedJobs = useCallback(() => {
    setActiveJobs((current) => current.filter((job) => (
      job.status === "pending" || job.status === "processing" || job.status === "starting"
    )));
  }, []);

  const activeJob = activeJobs.find((job) => job.status === "pending" || job.status === "processing" || job.status === "starting") || null;

  return (
    <>
      <Outlet context={{
        user,
        clientId,
        activeJob,
        activeJobs,
        setActiveJob,
        isMaintenanceMode,
        userPrivileges: null,
        oemBranding: null,
      }} />
      <GlobalJobNotifier
        jobs={activeJobs}
        onCancel={handleCancelJob}
        onClearFinished={handleClearFinishedJobs}
        latestLog=""
        className="uiux-runtime__job-notifier"
      />
    </>
  );
};

const LegacyMyPageEntry = () => {
  const location = useLocation();
  const user = getUser();
  return (
    <>
      <UiSurfaceEntryRedirect
        authenticatedUser={user}
        token={getToken()}
        expectedReleaseId={UIUX_THREE_TENANT_PILOT_RELEASE_ID}
        currentPath={location.pathname}
      />
      <MyPage />
    </>
  );
};

// Inner pilot surface. Mounted ONLY once PilotIdentityGate has established
// the user identity, so the live mount probe and LiveSessionProvider below
// never run without a resolvable tenant.
const UiUxPilotSurface = ({ user, token }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = resolveUiUxFunctionalRoute(location.pathname);
  const workspace = routeState.workspace;
  // Fail-safe surface selection: 'runtime' unless the bootstrap workspaces
  // map explicitly carries a 'live' value. Probing is skipped for users the
  // guards below redirect away, so their behavior is unchanged.
  const liveMount = useLiveMountDecision({
    enabled: UIUX_PILOT_ROLES.includes(user?.role) && !user?.oem_id,
  });

  if (!UIUX_PILOT_ROLES.includes(user?.role)) {
    return <Navigate to="/mypage" replace />;
  }
  if (user?.oem_id) {
    return <Navigate to="/mypage" replace />;
  }
  if (!routeState.valid) {
    return <Navigate to="/new/home" replace />;
  }

  const tool = routeState.tool;
  if (tool?.allowedRoles && !tool.allowedRoles.includes(user?.role)) {
    return <Navigate to={`/new/${workspace}`} replace />;
  }
  if (tool?.requireApiKeysSurfaceAccess && !canViewApiKeysSurface(user)) {
    return <Navigate to={`/new/${workspace}`} replace />;
  }
  const ToolComponent = tool?.component || null;
  const functionalModule = ToolComponent ? {
    id: tool.id,
    label: tool.label,
    description: tool.description,
    eyebrow: UIUX_FUNCTIONAL_TOOLS[tool.id]?.workspace === workspace ? "操作画面" : undefined,
    content: (
      <ToolComponent
        key={tool.id}
        {...(tool.id === "recipient-lists" ? {
          routeBase: "/new/targets/recipient-lists",
        } : {})}
        {...(tool.componentProps || {})}
      />
    ),
  } : null;

  return (
    <UiSurfaceBoundary
      authenticatedUser={user}
      token={token}
      expectedReleaseId={UIUX_THREE_TENANT_PILOT_RELEASE_ID}
    >
      <UiUxPilotErrorBoundary
        authenticatedUser={user}
        token={token}
        expectedReleaseId={UIUX_THREE_TENANT_PILOT_RELEASE_ID}
      >
        <Suspense fallback={<div role="status" aria-live="polite">新しい画面を読み込んでいます。</div>}>
          {liveMount.status !== "ready" ? (
            <div role="status" aria-live="polite">新しい画面を読み込んでいます。</div>
          ) : liveMount.surface === "live" ? (
            <UiUxLiveApp />
          ) : (
            <UiUxRuntimeApp
              authenticatedUser={user}
              token={token}
              expectedReleaseId={UIUX_THREE_TENANT_PILOT_RELEASE_ID}
              initialWorkspace={workspace}
              onWorkspaceChange={(nextWorkspace) => {
                if (UIUX_WORKSPACES.has(nextWorkspace)) navigate(`/new/${nextWorkspace}`);
              }}
              onOpenModule={(moduleId) => {
                const definition = UIUX_FUNCTIONAL_TOOLS[moduleId];
                if (definition) navigate(`/new/${definition.workspace}/${definition.path}`);
              }}
              functionalModule={functionalModule}
              onCloseModule={() => navigate(`/new/${workspace}`)}
            />
          )}
        </Suspense>
      </UiUxPilotErrorBoundary>
    </UiSurfaceBoundary>
  );
};

// /new pilot entry (2026-08-13 blank-mount fix). PilotIdentityGate keeps the
// route NEVER-BLANK: the same loading panel the pilot uses elsewhere renders
// while the legacy auth bootstrap (localStorage authToken/userInfo) is still
// establishing identity, and a bounded 10s wait falls back to the legacy
// /mypage surface instead of leaving an empty #root. The gate also enforces
// IDENTITY-BEFORE-HYDRATION: UiUxPilotSurface (and with it the live mount
// probe + LiveSessionProvider) mounts only after identity is established.
const UiUxPilotRoute = () => (
  <PilotIdentityGate
    pilotRoles={UIUX_PILOT_ROLES}
    pending={<div role="status" aria-live="polite">新しい画面を読み込んでいます。</div>}
    fallback={<Navigate to="/mypage" replace />}
  >
    {({ user, token }) => <UiUxPilotSurface user={user} token={token} />}
  </PilotIdentityGate>
);

const TERMINAL_SUBSCRIPTION_STATUSES = new Set([
  "canceled",
  "cancelled",
  "incomplete_expired",
  "unpaid",
]);

const normalizeSubscriptionValue = (value) => String(value || "").trim().toLowerCase();

export const getSubscriptionAuthStatus = (subscription) => {
  if (!subscription) return "no_subscription";

  const planTier = normalizeSubscriptionValue(subscription.plan_id_string);
  const localStatus = normalizeSubscriptionValue(subscription.status);
  const remoteStatus = normalizeSubscriptionValue(subscription.stripe_remote_status);

  // 無料プランへ移行済みなら、過去のStripe契約が終了済みでも無料枠は継続する。
  if (planTier === "free" && localStatus === "active") return "authorized";

  if (TERMINAL_SUBSCRIPTION_STATUSES.has(localStatus)) return "subscription_canceled";
  if (planTier !== "free" && TERMINAL_SUBSCRIPTION_STATUSES.has(remoteStatus)) {
    return "subscription_canceled";
  }

  return "authorized";
};

// --- RequireAuth Component ---
const RequireAuth = ({
  children,
  allowedRoles,
  requireApiKeysSurfaceAccess = false,
  requireOemApiKeysSurfaceAccess = false,
  authoritativeSurfaceAccess = false,
}) => {
  const [authStatus, setAuthStatus] = useState("checking");
  const location = useLocation();

  const token = useMemo(() => getToken(), []); 
  const user = useMemo(() => getUser(), []);   

  useEffect(() => {
    const currentToken = getToken();
    const currentUser = getUser();

    const verifyAccess = async () => {
      if (!currentToken || !currentUser || !currentUser.id) {
        setAuthStatus("not_logged_in");
        return;
      }

      // 1. Role checks (🚀 修正: oem_idを持たない本家管理者のみを即時許可)
      if (currentUser.role === "admin" && !currentUser.oem_id && !authoritativeSurfaceAccess) {
        setAuthStatus("authorized");
        return;
      }

      if (currentUser.oem_id && isOemRestrictedPath(location.pathname)) {
          console.warn(`[OEM GUARD] Unauthorized access attempt to: ${location.pathname}`);
          setAuthStatus("oem_unauthorized"); // 🚀 専用ステータスへ
          return;
      }

      // ロール自体が許可されていない場合はブロック
      if (!allowedRoles.includes(currentUser.role)) {
        setAuthStatus("unauthorized_role");
        return;
      }

      if (requireApiKeysSurfaceAccess && !canViewApiKeysSurface(currentUser)) {
        console.warn(`[Access Denied] API keys surface is hidden for: ${currentUser.role}`);
        setAuthStatus("unauthorized_permission");
        return;
      }

      if (requireOemApiKeysSurfaceAccess && !canViewOemApiKeysSurface(currentUser)) {
        console.warn(`[Access Denied] OEM API keys surface is hidden for: ${currentUser.role}`);
        setAuthStatus("unauthorized_permission");
        return;
      }

      if (requireOemApiKeysSurfaceAccess) {
        setAuthStatus("authorized");
        return;
      }

      // ★ 2. Permission checks (🚀 修正: Staff または oem_id を持つ Admin もチェック対象にする)
      if (
        !authoritativeSurfaceAccess
        && !requireApiKeysSurfaceAccess
        && !requireOemApiKeysSurfaceAccess
        && (
          currentUser.role === "staff"
          || currentUser.role === BOOKING_MANAGER_ROLE
          || (currentUser.role === "admin" && currentUser.oem_id)
        )
      ) {
          const currentPath = location.pathname;
          
          // menuDefs.js で定義したロジックを使って、権限があるかを確認する
          if (!hasPermission(currentUser, currentPath)) {
              console.warn(`[Access Denied] User missing permission for: ${currentPath}`);
              setAuthStatus("unauthorized_permission");
              return;
          }
      }

      // 日程調整専用ロールは、署名済みJWTとサーバー側のAPI許可リストで制御する。
      // 個人ユーザー単位の契約確認は行わない。
      if (shouldSkipSubscriptionCheck(currentUser)) {
        setAuthStatus("authorized");
        return;
      }

      if (currentUser.oem_id) {
        setAuthStatus("authorized");
        return;
      }

      // 3. Subscription check
      if (LOCAL_DEV_AUTH_BYPASS) {
        setAuthStatus("authorized");
        return;
      }

      try {
        const authHeader = getAuthHeader();
        if (!authHeader.Authorization) {
             setAuthStatus("not_logged_in");
             return;
        }

        const res = await fetch(`${API_URL}/api/subscription-status?userId=${currentUser.id}`, {
          headers: authHeader
        });

        // ★★★ 修正: レスポンスがOKでない場合（500エラー含む）の処理を強化 ★★★
        if (!res.ok) {
            console.warn("Subscription check failed (Server Error or Not Found). Redirecting to plan selection.");
            // サーバーエラー時も、管理者スタッフ以外はプラン選択へ飛ばす
            if (currentUser.role === "admin_staff") {
                setAuthStatus("authorized");
            } else {
                setAuthStatus("no_subscription");
            }
            return; 
        }

        const data = await res.json();

        if (data.success && data.subscription) {
          setAuthStatus(getSubscriptionAuthStatus(data.subscription));
        } else {
          if (currentUser.role === "admin_staff") {
            setAuthStatus("authorized"); 
          } else {
            setAuthStatus("no_subscription");
          }
        }
      } catch (error) {
        console.error("Subscription check error:", error);
        
        // ★★★ 重要な修正: JSONパースエラーや通信エラー時はプラン選択へ誘導 ★★★
        if (currentUser.role === "admin_staff") {
          setAuthStatus("authorized");
        } else {
          setAuthStatus("no_subscription"); 
        }
      }
    };

    verifyAccess();
  }, [token, user, allowedRoles, authoritativeSurfaceAccess, location.pathname, requireApiKeysSurfaceAccess, requireOemApiKeysSurfaceAccess]);

  if (authStatus === "checking") {
    return ( <div className="flex justify-center items-center h-screen"> <p>認証情報を確認中...</p> </div> );
  }
  if (authStatus === "not_logged_in") {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (authStatus === "unauthorized_role") {
    // ロール自体が許可されていない場合
    return <Navigate to={getDefaultAuthenticatedPath(user)} replace />;
  }
  if (authStatus === "unauthorized_permission") {
    // ★ 追加: スタッフだが、この機能の権限チェックボックスがOFFの場合
    // ダッシュボードに戻す（ダッシュボード権限もないスタッフは稀だが、その場合はループしないよう注意が必要）
    // 基本的にダッシュボード権限は必須と想定
    return <Navigate to={getDefaultAuthenticatedPath(user)} replace />;
  }
  if (authStatus === "oem_unauthorized") {
    return <Navigate to={getDefaultAuthenticatedPath(user)} replace />;
  }
  if (authStatus === "no_subscription") {
    if (user?.oem_id) return <OemUnavailable />;
    const allowedPaths = ["/plan-selection", "/payment-success", "/help", "/account"];
    if (allowedPaths.includes(location.pathname)) { return children; }
    return <Navigate to={withOperatorSurface("/plan-selection")} replace />;
  }
  if (authStatus === "subscription_canceled") {
    if (user?.oem_id) return <OemUnavailable />;
    const allowedPathsForCanceled = ["/account", "/help", "/plan-selection"];
    if (allowedPathsForCanceled.includes(location.pathname)) { return children; }
    return <Navigate to={withOperatorSurface("/plan-selection")} replace />;
  }
  if (authStatus === "authorized") {
    return children;
  }
  return <Navigate to="/login" replace />;
};

// --- Main App Component ---
function App() {
  return (
    <Router basename="/app">
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/client-session" element={<LoginPage />} />
        <Route path="/signup" element={isOemHost() ? <OemUnavailable /> : <SignupPage />} />
        <Route path="/invite" element={<Invite />} />
        <Route path="/sending" element={<SendingPage />} />
        <Route path="/signup-thanks" element={isOemHost() ? <OemUnavailable /> : <SignupThanksPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/quotation/:token" element={<QuotationViewPage />} />
        <Route path="/subscription/:token" element={<SubscriptionViewPage />} />
        <Route path="/public/form/:token" element={<PublicFormPage />} />
        <Route path="/public/booking/:publicId" element={<PublicBookingPage />} />
        <Route path="/public/booking/:publicId/manage/:appointmentPublicId" element={<PublicBookingManagePage />} />
        <Route path="/public/booking/:publicId/reschedule/:appointmentPublicId" element={<PublicBookingPage />} />
        <Route path="/public/booking/:publicId/complete/:appointmentPublicId" element={<PublicBookingCompletePage />} />
        <Route path="/sign" element={<PublicSignPage />} />
        {/* Legacy links remain routable; bootstrap scrubs the path token before mount. */}
        <Route path="/sign/:token" element={<PublicSignPage />} />

        <Route path="/agency/login" element={<AgencyClosed />} />
        <Route path="/agency/settlement/print" element={<AgencyClosed />} />
        <Route path="/agency/*" element={<AgencyClosed />} />

        {/* ★★★ ここにプラン選択と決済完了ページを追加 ★★★ */}
        <Route path="/plan-selection" element={shouldBlockOemPublicBilling("/plan-selection") ? <OemUnavailable /> : <PlanSelectionPage />} />
        <Route path="/payment-success" element={shouldBlockOemPublicBilling("/payment-success") ? <OemUnavailable /> : <PaymentSuccessPage />} />

        {/* Protected Routes */}
        <Route
          path="/new"
          element={
            <RequireAuth allowedRoles={UIUX_PILOT_ROLES} authoritativeSurfaceAccess>
              <UiUxPilotContextLayout />
            </RequireAuth>
          }
        >
          {/* Bare /new has no splat remainder, so without an index route the
              layout renders an empty Outlet — a blank page in production.
              Send it to the home view explicitly. */}
          <Route index element={<Navigate to="/new/home" replace />} />
          <Route path="*" element={<UiUxPilotRoute />} />
        </Route>

        <Route path="/" element={<AppLayout />}>
          <Route
            path="settings/booking"
            element={
              <RequireAuth allowedRoles={["admin", "admin_staff", "client_admin", BOOKING_MANAGER_ROLE]}>
                <BookingSettingsPage />
              </RequireAuth>
            }
          />
          <Route
            path="mypage"
            element={
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff", "metatest"]}>
                <LegacyMyPageEntry />
              </RequireAuth>
            }
          />
          
          <Route
            path="list-download"
            element={
              // ★ staff を追加
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff"]}>
                <ListDownloadPage />
              </RequireAuth>
            }
          />
          <Route
            path="list" 
            element={
              <RequireAuth allowedRoles={["admin"]}>
                <SalesList />
              </RequireAuth>
            }
          />
          <Route
            path="client-sales-lists" 
            element={
              // ★ staff を追加
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff"]}>
                <ClientSalesListPage />
              </RequireAuth>
            }
          />
          <Route
            path="admin/review-queue" 
            element={
              <RequireAuth allowedRoles={["admin", "client_admin", "admin_staff"]}>
                <ReviewDashboard />
              </RequireAuth>
            }
          />
          <Route
            path="admin/review-detail/:id" 
            element={
              <RequireAuth allowedRoles={["admin", "client_admin", "admin_staff"]}>
                <ReviewDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="admin/client-review-detail/:id" 
            element={
              <RequireAuth allowedRoles={["admin", "client_admin", "admin_staff"]}>
                <ReviewDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="templates"
            element={
              // ★ staff を追加
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff"]}>
                <Templates />
              </RequireAuth>
            }
          />
          <Route
            path="send"
            element={
              // ★ staff を追加
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff"]}>
                <SendPage />
              </RequireAuth>
            }
          />
          <Route
            path="settings"
            element={
              // ★ staff を追加
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff"]}>
                <SettingsPage />
              </RequireAuth>
            }
          />
          <Route
            path="history"
            element={
              // staff は元々入っていましたが維持
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff"]}>
                <HistoryPage />
              </RequireAuth>
            }
          />
          <Route
            path="click-logs"
            element={
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff"]}>
                <ClickLogsPage />
              </RequireAuth>
            }
          />
          <Route
            path="deals" 
            element={
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff", "metatest"]}>
                <DealsPage />
              </RequireAuth>
            }
          />
          <Route
            path="customer-ledger"
            element={
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff", "metatest"]}>
                <CustomerLedgerPage />
              </RequireAuth>
            }
          />
          <Route
            path="orders"
            element={
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff", "metatest"]}>
                <OrdersPage />
              </RequireAuth>
            }
          />
          <Route
            path="sales-analytics"
            element={
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff"]}>
                <SalesAnalytics />
              </RequireAuth>
            }
          />
          <Route
            path="users"
            element={
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff"]}>
                <UsersPage />
              </RequireAuth>
            }
          />
           <Route
            path="help"
            element={
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff"]}>
                <HelpPage />
              </RequireAuth>
            }
          />
          <Route
            path="account"
            element={
              // ★ staff を追加
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff"]}>
                <AccountPage />
              </RequireAuth>
            }
          />
          <Route
            path="account/free-plan-survey"
            element={
              <RequireAuth allowedRoles={["admin", "client_admin"]}>
                <FreePlanSurveyPage />
              </RequireAuth>
            }
          />
          <Route
            path="invoices"
            element={
              // ★ staff を追加
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff"]}>
                <InvoicesPage />
              </RequireAuth>
            }
          />
          <Route
            path="list-templates"
            element={
              // ★ staff を追加
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff"]}>
                <ListTemplatesPage />
              </RequireAuth>
            }
          />
          <Route
            path="list-templates/new"
            element={
              // ★ staff を追加
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff"]}>
                <EditListTemplatePage />
              </RequireAuth>
            }
          />
          <Route
            path="list-templates/edit/:id"
            element={
              // ★ staff を追加
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff"]}>
                <EditListTemplatePage />
              </RequireAuth>
            }
          />
          <Route
            path="extension" 
            element={
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff"]}>
                <ExtensionPage />
              </RequireAuth>
            }
          />
          <Route
            path="api-keys"
            element={
              <RequireAuth allowedRoles={API_KEYS_SURFACE_ROLES} requireApiKeysSurfaceAccess>
                <ApiKeysPage />
              </RequireAuth>
            }
          />
          <Route
            path="oem/api-keys"
            element={
              <RequireAuth allowedRoles={API_KEYS_SURFACE_ROLES} requireOemApiKeysSurfaceAccess>
                <ApiKeysPage surface="oem" />
              </RequireAuth>
            }
          />
          <Route
            path="api-keys-en"
            element={
              <RequireAuth allowedRoles={API_KEYS_SURFACE_ROLES} requireApiKeysSurfaceAccess>
                <ApiKeysEnPage />
              </RequireAuth>
            }
          />
          <Route
            path="admin/announcements"
            element={
              <RequireAuth allowedRoles={["admin"]}>
                <AdminAnnouncementsPage />
              </RequireAuth>
            }
          />
          <Route
            path="admin/api-keys"
            element={
              <RequireAuth allowedRoles={["admin"]}>
                <Navigate to="/api-keys" replace />
              </RequireAuth>
            }
          />
          <Route
            path="inquiry-management" 
            element={
              <RequireAuth allowedRoles={["admin", "admin_staff"]}>
                <InquiryManagementPage />
              </RequireAuth>
            }
          />
          <Route
            path="client-management" 
            element={
              <RequireAuth allowedRoles={["admin", "admin_staff"]}>
                <ClientManagementPage />
              </RequireAuth>
            }
          />
          <Route
            path="pending-users" 
            element={
              <RequireAuth allowedRoles={["admin", "admin_staff"]}>
                <PendingUsersPage />
              </RequireAuth>
            }
          />
          <Route
            path="marketing/forms"
            element={
              // ★ staff を追加
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff"]}>
                <MarketingFormsPage />
              </RequireAuth>
            }
          />
          <Route
            path="marketing/leads"
            element={
              // staff は元々あり
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff", "metatest"]}>
                <MarketingLeadsPage />
              </RequireAuth>
            }
          />
          <Route
            path="marketing/email"
            element={
              // ★ staff を追加
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff"]}>
                <MarketingEmailPage />
              </RequireAuth>
            }
          />
          <Route
            path="marketing/scenarios"
            element={
              // ★ staff を追加
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff"]}>
                <MarketingScenariosPage />
              </RequireAuth>
            }
          />
          <Route
            path="marketing/tracking"
            element={
              // ★ staff を追加
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff", "metatest"]}>
                <MarketingTrackingPage />
              </RequireAuth>
            }
          />
          <Route
            path="marketing/lps"
            element={
              // ★ staff を追加
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff"]}>
                <MarketingLpPage />
              </RequireAuth>
            }
          />
          <Route
            path="marketing/presentations"
            element={
              <RequireAuth allowedRoles={["admin", "client_admin", "staff", "admin_staff"]}>
                <MarketingPresentationPage />
              </RequireAuth>
            }
          />
          <Route
            path="marketing/meta-dashboard"
            element={
              <RequireAuth allowedRoles={["admin", "metatest"]}>
                <MarketingMetaDashboard />
              </RequireAuth>
            }
          />
          
          {/* システム管理系: staffは不可 */}
          <Route
            path="admin/vps-status"
            element={
              <RequireAuth allowedRoles={["admin"]}>
                <VpsStatusDashboard />
              </RequireAuth>
            }
          />
          <Route
            path="admin/vps-management"
            element={
              <RequireAuth allowedRoles={["admin"]}>
                <VpsManagementPage />
              </RequireAuth>
            }
          />
          <Route
            path="admin/learning-queue" 
            element={
              <RequireAuth allowedRoles={["admin", "admin_staff"]}>
                <AdminLearningQueue />
              </RequireAuth>
            }
          />
          {/* ----- End Admin Only Routes ----- */}

          <Route index element={<AuthenticatedIndexRedirect />} />
        </Route> 

        <Route path="/ui-preview/*" element={<UiPreviewReloadBridge />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
